package com.sweetlime.dungeonamigo.presentation.ui.components

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Check
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.sweetlime.dungeonamigo.presentation.ui.theme.BrandColors

private val PORTRAIT_COLORS = listOf(
    0xFFD4A843L to "Parchment",
    0xFFE84545L to "Ember",
    0xFF4A90D9L to "Spell Blue",
    0xFF6B4EFFL to "Purple",
    0xFF2ECC71L to "Emerald",
    0xFFE67E22L to "Burnt Orange",
    0xFF1ABC9CL to "Teal",
    0xFFE74C3CL to "Crimson",
    0xFFF1C40FL to "Yellow",
    0xFFECF0F1L to "Silver",
)

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun PortraitPickerSheet(
    characterClass: String,
    initialPrimary: Long,
    initialSecondary: Long,
    onSave: (primary: Long, secondary: Long) -> Unit,
    onDismiss: () -> Unit,
) {
    var selectedPrimary by remember { mutableStateOf(initialPrimary) }
    var selectedSecondary by remember { mutableStateOf(initialSecondary) }
    val sheetState = rememberModalBottomSheetState(skipPartiallyExpanded = true)

    ModalBottomSheet(
        onDismissRequest = onDismiss,
        sheetState = sheetState,
        containerColor = BrandColors.Surface,
        dragHandle = {
            Box(
                Modifier
                    .padding(vertical = 8.dp)
                    .size(32.dp, 4.dp)
                    .clip(CircleShape)
                    .background(BrandColors.SurfaceLight),
            )
        },
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .verticalScroll(rememberScrollState())
                .padding(horizontal = 20.dp)
                .padding(bottom = 32.dp),
            verticalArrangement = Arrangement.spacedBy(20.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            Text(
                "Customize Portrait",
                color = BrandColors.TextPrimary,
                fontSize = 18.sp,
                fontWeight = FontWeight.Bold,
            )

            CharacterPortrait(
                characterClass = characterClass,
                primaryColor = Color(selectedPrimary),
                secondaryColor = Color(selectedSecondary),
                size = PortraitSize.XLARGE,
            )

            ColorSection(
                label = "Primary Color",
                sublabel = "armor / main",
                selectedLong = selectedPrimary,
                onSelect = { selectedPrimary = it },
            )

            ColorSection(
                label = "Secondary Color",
                sublabel = "cloak / accent",
                selectedLong = selectedSecondary,
                onSelect = { selectedSecondary = it },
            )

            Button(
                onClick = { onSave(selectedPrimary, selectedSecondary); onDismiss() },
                modifier = Modifier.fillMaxWidth().height(52.dp),
                colors = ButtonDefaults.buttonColors(containerColor = BrandColors.Ember),
                shape = RoundedCornerShape(12.dp),
            ) {
                Text("Save Portrait", fontSize = 16.sp, fontWeight = FontWeight.Bold, color = BrandColors.TextPrimary)
            }
        }
    }
}

@Composable
private fun ColorSection(
    label: String,
    sublabel: String,
    selectedLong: Long,
    onSelect: (Long) -> Unit,
) {
    Column(
        modifier = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(10.dp),
    ) {
        Column {
            Text(label, color = BrandColors.TextPrimary, fontSize = 14.sp, fontWeight = FontWeight.SemiBold)
            Text(sublabel, color = BrandColors.TextSecondary, fontSize = 12.sp)
        }
        // Two rows of 5 swatches each
        val rows = PORTRAIT_COLORS.chunked(5)
        rows.forEach { row ->
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(10.dp),
            ) {
                row.forEach { (longValue, name) ->
                    val isSelected = selectedLong == longValue
                    Box(
                        modifier = Modifier
                            .weight(1f)
                            .aspectRatio(1f)
                            .clip(CircleShape)
                            .background(Color(longValue))
                            .border(
                                width = if (isSelected) 3.dp else 1.dp,
                                color = if (isSelected) BrandColors.TextPrimary else Color.Transparent,
                                shape = CircleShape,
                            )
                            .clickable { onSelect(longValue) },
                        contentAlignment = Alignment.Center,
                    ) {
                        if (isSelected) {
                            Icon(
                                Icons.Filled.Check,
                                contentDescription = name,
                                tint = BrandColors.Background,
                                modifier = Modifier.size(16.dp),
                            )
                        }
                    }
                }
            }
        }
    }
}
